import { useEffect, useState } from 'react';
import { AlertTriangle, Check, RefreshCw, X } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Spinner } from '../../components/ui/Spinner';
import { measureSms, SMS_SOFT_MAX_CHARS } from '../../lib/leadBot/smsDraft';
import { SOURCE_LABELS, TRIGGER_LABELS } from '../../lib/leadBot/queueView';
import type { BotMessage } from '../../types/leadBot';

// The approval control. ⚠️ THE ONLY PLACE IN THE APP THAT CAN CAUSE AN SMS TO A
// MEMBER OF THE PUBLIC, so it is built to be read before it is clicked:
//
//   - the draft is EDITABLE in place, because the common case is a small fix
//     and forcing a reject-and-rewrite would make people approve things they
//     would rather have tweaked;
//   - the SMS length and segment count are shown, because a two-segment reply
//     costs twice a one-segment one and a single emoji halves the limit;
//   - a compliance trigger is rendered as a BLOCKING-looking warning above the
//     text, not a quiet chip, because it means the bot was forbidden from
//     answering and the AM is about to send on that basis.

export function DraftApprovalPanel({
  draft,
  smsConfigured,
  onApprove,
  onReject,
  onRegenerate,
  busy,
  autonomous = false,
}: {
  draft: BotMessage;
  smsConfigured: boolean;
  onApprove: (text: string) => void;
  onReject: () => void;
  onRegenerate: () => void;
  busy: boolean;
  /**
   * ⚠️ When true this is NOT an approval queue. An autonomous bot sends on its
   * own, so a draft still sitting here means the automatic send did not happen
   * — the panel says so and offers to push it, rather than implying someone was
   * supposed to be reviewing it.
   */
  autonomous?: boolean;
}) {
  const [text, setText] = useState(draft.text);
  // Re-seed when a NEW draft arrives (a regenerate, or a reply to a new turn) —
  // keyed on the draft id so an AM's in-progress edit is never wiped by a poll.
  useEffect(() => {
    setText(draft.text);
  }, [draft.id, draft.text]);

  const measured = measureSms(text);
  const edited = text.trim() !== draft.text.trim();
  const compliance = draft.handoffTriggers.find((t) => t.kind === 'compliance');
  const others = draft.handoffTriggers.filter((t) => t.kind !== 'compliance');

  return (
    <div className="shrink-0 border-t bg-card p-4">
      {autonomous && (
        <p className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
          This bot replies automatically. This reply did <strong>not</strong> go out on its own —
          most likely the number is not on the test phone allowlist, or a cap or an opt-out
          stopped it. Sending it here is a deliberate exception.
        </p>
      )}
      {compliance && (
        // ⚠️ Loud on purpose. This says the bot was NOT ALLOWED to answer the
        // question — the AM is approving an acknowledgement and a handover, and
        // if they edit it into an answer they are the one advising on licence or
        // towing compliance.
        <div className="mb-3 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">{TRIGGER_LABELS.compliance}</p>
            <p className="mt-0.5">
              The lead {compliance.detail} ({SOURCE_LABELS[compliance.source]}). The assistant
              was told not to answer it. Do not edit this reply into an answer — the correct
              response depends on the specific vehicle and on the customer's own licence, and
              only a person can give it.
            </p>
          </div>
        </div>
      )}

      {others.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {others.map((trigger) => (
            <Badge key={trigger.kind} variant="warning">
              {TRIGGER_LABELS[trigger.kind]}
            </Badge>
          ))}
        </div>
      )}

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        className="resize-none"
        aria-label="Draft reply"
        disabled={busy}
      />

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {measured.characters} characters · {measured.segments} SMS segment
          {measured.segments === 1 ? '' : 's'} · {measured.encoding}
          {measured.characters > SMS_SOFT_MAX_CHARS && (
            <span className="ml-1 text-amber-700">— long for a text message</span>
          )}
          {edited && <span className="ml-1 text-kyck-700">· edited</span>}
        </p>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRegenerate}
            disabled={busy}
            title="Ask the assistant for a different reply"
          >
            <RefreshCw className="h-4 w-4" />
            Redraft
          </Button>
          <Button variant="outline" size="sm" onClick={onReject} disabled={busy}>
            <X className="h-4 w-4" />
            Reject
          </Button>
          <Button
            size="sm"
            onClick={() => onApprove(text)}
            disabled={busy || text.trim() === '' || !smsConfigured}
            title={
              smsConfigured
                ? 'Send this message to the lead'
                : 'The bot has no SMS number yet — nothing can be sent'
            }
          >
            {busy ? <Spinner className="h-4 w-4" /> : <Check className="h-4 w-4" />}
            {autonomous ? 'Send anyway' : 'Approve & send'}
          </Button>
        </div>
      </div>

      {!smsConfigured && (
        // ⚠️ Stated plainly rather than left to a failed send. The number is
        // provisioned separately, so this is a normal state during setup.
        <p className="mt-2 text-xs text-amber-700">
          The lead bot has no SMS number yet, so nothing can be sent. Drafts keep queueing
          here and can be approved the moment the number is provisioned.
        </p>
      )}
    </div>
  );
}
